import { useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Fade,
  Grid2,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Info as InfoIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { LoadDashboardOutput, ValidationError } from "baptize-america-client";
import { useAlert } from "../../components/AlertProvider";
import { LoginWindow } from "../../components/LoginWindow";
import TopNavBar from "../../components/TopNavBar";
import { loadDashboard } from "../../utils/client";
import { isAdmin } from "../../utils/helper";
import { useUserData } from "../../components/DataProvider";

const Dashboard = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { setAlert } = useAlert();
  const [dashboard, setDashboard] = useState<LoadDashboardOutput>();
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { authStatus } = useAuthenticator((context) => [context.user]);
  const { attributes } = useUserData();

  const styles = {
    pwBox: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column",
      width: "100%",
      height: "50vh",
      mt: 5,
    },
    button: {
      height: 60,
      width: 300,
      fontSize: 16,
    },
    navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexDirection: "column",
      mb: 5,
    },
    dashboardCard: {
      p: 2,
      borderRadius: 4,
      border: 1,
      borderColor: "#CCCCCC",
      height: isLargeScreen ? 180 : null,
    },
  };

  const getDashboard = async () => {
    setLoading(true);
    if (!isAdmin(attributes)) {
      setAlert({
        showAlert: true,
        alertMsg: "Only Admins can view dashboard",
        alertType: "error",
      });
      setLoading(false);
      return;
    }

    try {
      const result = await loadDashboard({ password: password });
      setTimeout(() => {
        setLoading(false);
        setDashboard(result);
      }, 500);
    } catch (err) {
      setDashboard(undefined);
      setLoading(false);
      console.log("Error loading dashboard", err);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Loading Dashboard",
        alertType: "error",
      });
    }
  };

  const DashboardCard = ({
    title,
    loading,
    count,
    description,
    moreInfo,
  }: {
    title: string;
    loading: boolean;
    count: number;
    description: string;
    moreInfo?: string;
  }) => {
    return (
      <Box sx={[styles.dashboardCard]}>
        <Box>
          <Box
            sx={{
              textAlign: "left",
              "& .MuiIconButton-root": {
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: 1,
              },
            }}
          >
            <Typography variant={"h6"} sx={{ fontSize: "1.2rem" }}>
              {title}
              {moreInfo && (
                <Tooltip
                  enterTouchDelay={20}
                  leaveTouchDelay={3000}
                  title={moreInfo}
                >
                  <IconButton>
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
            <Fade in={loading} unmountOnExit>
              <CircularProgress size={29.5} />
            </Fade>
            <Fade timeout={1000} in={!loading} unmountOnExit>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
                component="span"
              >
                {count}
              </Typography>
            </Fade>
            <Typography sx={{ fontSize: "1rem" }}>{description}</Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return authStatus === "authenticated" && isAdmin(attributes) ? (
    <div>
      <Container maxWidth={isLargeScreen ? "md" : false}>
        <Box
          sx={[
            {
              "& .MuiTextField-root": {
                m: 1,
                width: theme.breakpoints.values.sm,
              },
            },
            styles.navBar,
          ]}
        >
          <Box sx={{ mb: 4 }}>
            <TopNavBar showBackArrow={true} />
          </Box>
          {dashboard ? (
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Registered Churches"}
                  count={dashboard?.churchCount ?? 0}
                  loading={loading}
                  description={
                    "The total number of Churches that have signed up for Baptize America."
                  }
                  moreInfo={
                    "Churches that accepted the invitation to register for Baptize America in-app"
                  }
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Pending Invitations"}
                  count={dashboard?.pendingInvitationCount ?? 0}
                  loading={loading}
                  description={
                    "The total number Church Invites that have been sent, but not yet used to register in-app."
                  }
                  moreInfo={`Invitations expire after 21 days and will no longer be counted.
                    If an invitation is sent twice they will be counted as separate invitations.
                    When an invite is activated, the other invite will remain in this list until 21 days have passed.`}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Baptism Events"}
                  count={dashboard?.baptismEventCount ?? 0}
                  loading={loading}
                  description={
                    "The total number of Church Services with Baptisms during Baptize America."
                  }
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Baptism Registrations"}
                  count={dashboard?.totalRegistrationCount ?? 0}
                  loading={loading}
                  description={
                    "The total number of people that have signed up to be baptized during a Church Service."
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Reported Baptisms"}
                  count={dashboard?.totalReportedBaptismPerformed ?? 0}
                  loading={loading}
                  description={
                    "The total number of baptisms performed during Baptize America."
                  }
                  moreInfo={"Self reported by each church manager."}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Reported Salvations"}
                  count={dashboard?.totalReportedSalvationCount ?? 0}
                  loading={loading}
                  description={
                    "The total number of salvations reported during Baptize America."
                  }
                  moreInfo={"Self reported by each church manager."}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <DashboardCard
                  title={"Generated Certificates"}
                  count={dashboard?.certificateCount ?? 0}
                  loading={loading}
                  description={"The total number of generated certificates"}
                  moreInfo={"Total times the download button is clicked."}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    height: 60,
                    fontSize: 16,
                  }}
                  loading={loading}
                  disabled={loading}
                  loadingIndicator={<CircularProgress size={30} />}
                  onClick={() => getDashboard()}
                >
                  Reload Dashboard
                </Button>
              </Grid2>
            </Grid2>
          ) : (
            <Box
              sx={styles.pwBox}
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={async (e) => {
                e.preventDefault();
                await getDashboard();
              }}
            >
              <TextField
                required
                disabled={loading}
                type={showPassword ? "text" : "password"}
                id="password"
                label="Password"
                value={password}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword
                              ? "hide the password"
                              : "display the password"
                          }
                          onMouseDown={() => setShowPassword(true)}
                          onMouseUp={() => setShowPassword(false)}
                          onTouchStart={() => setShowPassword(true)}
                          onTouchEnd={() => setShowPassword(false)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                onChange={(e) => setPassword(e.target.value)}
                sx={styles.button}
              />
              <Button
                type="submit"
                disabled={loading}
                loading={loading}
                loadingIndicator={<CircularProgress size={30} />}
                sx={styles.button}
                variant="outlined"
                onClick={() => getDashboard()}
              >
                Load Dashboard
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </div>
  ) : (
    <LoginWindow />
  );
};

export default Dashboard;
